import React, { useState, useEffect } from 'react';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import AdminHeaderNavigation from '../../components/ui/AdminHeaderNavigation';
import CalendarView from './components/CalendarView';
import TimeSlotManager from './components/TimeSlotManager';
import BulkActionsPanel from './components/BulkActionsPanel';
import QuickStatsCard from './components/QuickStatsCard';
import repositoryContainer from '../../services/RepositoryContainer';
import { useAuth } from '../../components/context/AuthContext';
import { useToast } from '../../components/context/ToastContext';
import Icon from '../../components/AppIcon';



const AvailabilityManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const [availabilityData, setAvailabilityData] = useState({});

  const [timeSlots, setTimeSlots] = useState([]);
  const [isSavingSlot, setIsSavingSlot] = useState(false);
  const [isDeletingSlot, setIsDeletingSlot] = useState(false);
  const { addToast } = useToast();

  const [stats, setStats] = useState({
    availableDays: 0,
    totalSlots: 0,
    bookedSlots: 0,
    blockedDays: 0
  });

  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  // Get admin calendar data (detailed availability)
  const fetchAvailability = async (startDate, endDate) => {
    try {
      const response = await repositoryContainer.dateSlot.getAdminCalendar(
        startDate, // "2025-02-01"
        endDate // "2025-02-28"
      );
      return response.dates || {};
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };

  const formatIsoDate = (date) => {
    if (!date) return null;
    return new Date(date).toISOString().split('T')[0];
  };

  const getDateSlotIdForDate = (date) => {
    const key = formatIsoDate(date);
    const dayData = availabilityData?.[key];
    // Backend payload expects a "dateSlotId" UUID.
    return dayData?.dateSlotId || dayData?.id || null;
  };
// Get admin time slots for a specific date
const fetchTimeSlots = async (date) => {
  try {
    const isoDate = formatIsoDate(date);
    if (!isoDate) return [];

    const slots = await repositoryContainer.dateSlot.getAdminTimeSlots(isoDate);
    return slots;
  } catch (error) {
    console.error('Failed to fetch time slots:', error);
  }
};

// Admin: Get dashboard stats
const fetchDashboardStats = async (startDate, endDate) => {
  try {
    const stats = await repositoryContainer.dateSlot.getStats(startDate, endDate);
    return stats;
  } catch (error) {
    console.error('Failed to fetch stats:', error);
  }
};

// Admin: Set working hours
const setWorkingHours = async (date, startTime, endTime) => {
  try {
    const response = await repositoryContainer.dateSlot.setWorkingHours(
      date,      // "2025-02-15"
      startTime, // "09:00"
      endTime    // "17:00"
    );
    return response;
  } catch (error) {
    console.error('Failed to set working hours:', error);
  }
};

  const handleDateSelect = (date) => {
    const dateSlotId = getDateSlotIdForDate(date);
    setSelectedDate({ date, dateSlotId });
  };

  // load helpers
  const loadAvailabilityData = async (startDate, endDate) => {
    const data = await fetchAvailability(startDate, endDate);
    if (data) setAvailabilityData(data);
  };

  const loadTimeSlotsForDate = async (date) => {
    const data = await fetchTimeSlots(date);
    if (Array.isArray(data)) setTimeSlots(data);
  };

  const loadStats = async (startDate, endDate) => {
    const data = await fetchDashboardStats(startDate, endDate);
    if (data) setStats(data);
  };

  const handleMonthChange = async ({ startDate, endDate }) => {
    await loadAvailabilityData(startDate, endDate);
    await loadStats(startDate, endDate);
  };

  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .split('T')[0];
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];

    handleMonthChange({ startDate: start, endDate: end });
  }, []);

  useEffect(() => {
    if (selectedDate?.date) loadTimeSlotsForDate(selectedDate.date);
  }, [selectedDate]);

  const handleAddSlot = async (newSlot) => {
    if (!selectedDate?.date) return;

    const dateSlotId = selectedDate?.dateSlotId || getDateSlotIdForDate(selectedDate.date);
    if (!dateSlotId) {
      console.warn('Cannot create timeslot: missing dateSlotId for selected date');
      return;
    }

    setIsSavingSlot(true);
    try {
      await repositoryContainer.timeSlot.create({
        dateSlotId,
        startTime: newSlot.startTime,
        endTime: newSlot.endTime,
        capacity: newSlot.capacity,
        serviceItemIds: newSlot.service === 'all' ? [] : [newSlot.service]
      });

      await loadTimeSlotsForDate(selectedDate.date);
      
      // Refresh availability data for current month
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split('T')[0];
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];
      await loadAvailabilityData(start, end);
      await loadStats(start, end);
    } catch (error) {
      console.error('Failed to create time slot:', error);
      addToast(error?.data?.message ||'Failed to add time slot. Please try again.', 'error');
    } finally {
      setIsSavingSlot(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    setIsDeletingSlot(true);
    try {
      await repositoryContainer.timeSlot.delete(slotId);
      await loadTimeSlotsForDate(selectedDate.date);
      
      // Refresh availability data for current month
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split('T')[0];
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];
      await loadAvailabilityData(start, end);
      await loadStats(start, end);
    } catch (error) {
      console.error('Failed to delete time slot:', error);
      addToast(error?.data?.message  || 'Failed to delete time slot. Please try again.', 'error');
    } finally {
      setIsDeletingSlot(false);
    }
  };

  const handleUpdateSlot = async (slotId, updates) => {
    try {
      await repositoryContainer.timeSlot.update(slotId, updates);
      // Update local state after successful API call
      setTimeSlots(timeSlots?.map(slot => 
        slot?.id === slotId ? { ...slot, ...updates } : slot
      ));
    } catch (error) {
      console.error('Failed to update time slot:', error);
      addToast(error?.data?.message  || 'Failed to update time slot. Please try again.', 'error');
    }
  };

  const refreshCurrentMonth = async () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .split('T')[0];
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .split('T')[0];

    await loadAvailabilityData(start, end);
    await loadStats(start, end);

    if (selectedDate?.date) {
      await loadTimeSlotsForDate(selectedDate.date);
    }
  };

  const handleBulkAction = async (actionData) => {
    await refreshCurrentMonth();
  };

    const [isRestoring, setIsRestoring] = useState(false);
  
    const handleRestore = async () => {
      setIsRestoring(true);
      try {
        await repositoryContainer.company.restoreBusiness();
        // Force a reload to refresh the user context and token from the backend
        window.location.reload(); 
      } catch (err) {
        console.error('Failed to restore business:', err);
        alert('Could not restore the business. Please contact support.');
        setIsRestoring(false);
      }
    };
  
  if (user?.company && user.company.isActive === false) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;700&display=swap');
  
          .archive-container {
            font-family: 'DM Sans', sans-serif;
            color: #fff;
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            position: relative;
            background: #000;
          }
  
          .bg-overlay {
            position: absolute;
            inset: 0;
            background-image: url('https://weyyfslmfqjlkvtfwunm.supabase.co/storage/v1/object/public/Claw/Herosection/heroImage.png');
            background-size: cover;
            background-position: center;
            filter: blur(15px) brightness(0.4);
            opacity: 0.6;
            z-index: 0;
          }
  
          .archive-card {
            position: relative;
            z-index: 10;
            width: 100%;
            max-width: 480px;
            background: rgba(255, 255, 255, 0.03);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 24px;
            padding: 48px 32px;
            text-align: center;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          }
  
          .status-icon-wrap {
            width: 80px;
            height: 80px;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 24px;
            border: 1px solid rgba(255, 255, 255, 0.1);
          }
  
          .archive-badge {
            display: inline-block;
            padding: 4px 12px;
            background: rgba(234, 179, 8, 0.1);
            border: 1px solid rgba(234, 179, 8, 0.3);
            color: #eab308;
            border-radius: 99px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            margin-bottom: 20px;
          }
  
          .archive-title {
            font-family: 'DM Serif Display', serif;
            font-size: 32px;
            margin-bottom: 12px;
            letter-spacing: -0.02em;
          }
  
          .archive-desc {
            font-size: 15px;
            color: rgba(255, 255, 255, 0.6);
            line-height: 1.6;
            margin-bottom: 32px;
            font-weight: 300;
          }
  
          .info-row {
            background: rgba(255, 255, 255, 0.03);
            border-radius: 12px;
            padding: 16px;
            text-align: left;
            margin-bottom: 32px;
            border: 1px solid rgba(255, 255, 255, 0.05);
          }
  
          .info-item {
            display: flex;
            gap: 12px;
            font-size: 13px;
            color: rgba(255, 255, 255, 0.8);
            margin-bottom: 8px;
          }
  
          .restore-btn {
            width: 100%;
            background: #fff;
            color: #000;
            border: none;
            padding: 16px;
            border-radius: 12px;
            font-weight: 700;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
            margin-bottom: 12px;
          }
  
          .restore-btn:hover {
            background: rgba(255, 255, 255, 0.9);
            transform: translateY(-2px);
          }
  
          .signout-btn {
            width: 100%;
            background: transparent;
            color: rgba(255, 255, 255, 0.5);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 16px;
            border-radius: 12px;
            font-weight: 500;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
          }
  
          .signout-btn:hover {
            background: rgba(255, 255, 255, 0.05);
            color: #fff;
          }
        `}</style>
  
        <div className="archive-container">
          <div className="bg-overlay" />
          
          <div className="archive-card">
            <div className="status-icon-wrap">
              <Icon name="Archive" size={32} color="rgba(255,255,255,0.7)" />
            </div>
  
            <div className="archive-badge">Status: Archived</div>
            
            <h1 className="archive-title">Workspace Offline</h1>
            <p className="archive-desc">
              Your business profile for <strong>{user?.company?.name || 'your store'}</strong> has been deactivated. All public booking services and dashboard tools are currently restricted.
            </p>
  
            <div className="info-row">
              <div className="info-item">
                <Icon name="EyeOff" size={16} color="#eab308" />
                <span>Public booking page is hidden</span>
              </div>
              <div className="info-item">
                <Icon name="Database" size={16} color="#eab308" />
                <span>Data is preserved for compliance</span>
              </div>
              <div className="info-item">
                <Icon name="HelpCircle" size={16} color="#eab308" />
                <span>Contact support@clawappz.com</span>
              </div>
            </div>
  
            <button className="restore-btn" onClick={handleRestore} disabled={isRestoring}>
              {isRestoring ? 'Processing Restoration...' : 'Reactivate Business'}
            </button>
            
            <button className="signout-btn" onClick={logout}>
              Log Out
            </button>
          </div>
        </div>
      </>
    );
  }


  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation 
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div className={`transition-all duration-250 ease-elegant ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <AdminHeaderNavigation 
          userName="Sarah Johnson"
          onLogout={handleLogout}
          isSidebarCollapsed={isSidebarCollapsed}
        />

        <main className="pt-24 px-4 md:px-6 lg:px-8 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6 md:mb-8">
              <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                Availability Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground font-caption">
                Control your appointment scheduling and manage time slots efficiently
              </p>
            </div>

            <div className="mb-6 md:mb-8">
              <QuickStatsCard stats={stats} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                <CalendarView
                  selectedDate={selectedDate?.date}
                  onDateSelect={handleDateSelect}
                  availabilityData={availabilityData}
                  onMonthChange={handleMonthChange}
                />

                <TimeSlotManager
                  selectedDate={selectedDate}
                  timeSlots={timeSlots}
                  onAddSlot={handleAddSlot}
                  onDeleteSlot={handleDeleteSlot}
                  onUpdateSlot={handleUpdateSlot}
                  isSaving={isSavingSlot}
                  isDeleting={isDeletingSlot}
                  onCopySuccess={refreshCurrentMonth}
                />
              </div>

              <div className="lg:col-span-1">
                <div className="lg:sticky lg:top-24">
                  <BulkActionsPanel onBulkAction={handleBulkAction} />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AvailabilityManagement;
